import { useCallback, useState } from "react";
import { useApolloClient, useMutation, useQuery } from "@apollo/client";

import {
  ACTIVITY_FEED,
  ACTIVITY_FEED_PIN,
  ACTIVITY_FEED_UNPIN,
} from "MVVM/GraphQL/homepage";
import { IActivityFeedNode } from "./@types";

export const MAX_PINNED_ITEMS = 3;

// Smaller first page for a faster initial paint; later pages go bigger since
// useInfiniteScroll's early trigger means they load in the background well
// before the user reaches them.
const INITIAL_PAGE_SIZE = 12;
const NEXT_PAGE_SIZE = 25;

/* crypto.randomUUID is absent on some older Safari versions still in the
   wild — fall back to a timestamp-suffixed random id. */
const generateSeed = (): string => {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    try {
      return crypto.randomUUID();
    } catch {
      /* fall through */
    }
  }
  return `s_${Date.now().toString(36)}_${Math.random()
    .toString(36)
    .slice(2, 10)}`;
};

/* Module-level, not a per-mount ref: leaving the homepage (to a clip page,
   say) unmounts the feed, and a fresh seed on the way back reshuffles
   everything and orphans the cache entry holding every page the viewer had
   already scrolled through. Surviving the unmount is what lets back-
   navigation come out of the cache instead of refetching from page one.
   A real page load still starts a new session, and so still reshuffles. */
let sessionSeed: string | undefined;

export const useActivityFeed = () => {
  if (!sessionSeed) sessionSeed = generateSeed();
  const seed = sessionSeed;

  const { data, loading, error, fetchMore, refetch } = useQuery(ACTIVITY_FEED, {
    variables: { seed, first: INITIAL_PAGE_SIZE },
    /* Deliberately not cache-and-network. fetchMore()'s updateQuery merges
       every loaded page back into this query's cache entry, but the network
       half of cache-and-network re-requests `first: INITIAL_PAGE_SIZE` and
       overwrites that merged list — so returning to a deep feed would render
       all of it, then visibly snap back to the first 12 items. */
    fetchPolicy: "cache-first",
    // Without this, `loading` only reflects the initial fetch — fetchMore()
    // wouldn't flip it back to true, so nothing gated useInfiniteScroll's
    // isLoading check against firing loadMore again while a page was still
    // in flight (more of a risk now that threshold triggers it earlier).
    notifyOnNetworkStatusChange: true,
  });

  const [loadMoreError, setLoadMoreError] = useState<string | null>(null);

  const pageInfo = data?.activityFeed?.pageInfo;

  const loadMore = useCallback(() => {
    if (!pageInfo?.hasNextPage || !pageInfo?.endCursor) return;
    setLoadMoreError(null);

    // No cache typePolicy exists for `activityFeed` (see App.js), so a bare
    // fetchMore() would store the `after`-cursor page under a distinct cache
    // key instead of appending to it. updateQuery makes the merge explicit.
    fetchMore({
      variables: {
        seed,
        first: NEXT_PAGE_SIZE,
        after: pageInfo.endCursor,
      },
      updateQuery: (previousResult, { fetchMoreResult }) => {
        if (!fetchMoreResult) return previousResult;
        return {
          activityFeed: {
            ...fetchMoreResult.activityFeed,
            nodes: [
              ...(previousResult.activityFeed?.nodes ?? []),
              ...(fetchMoreResult.activityFeed?.nodes ?? []),
            ],
          },
        };
      },
    }).catch(() => {
      setLoadMoreError("Couldn't load more.");
    });
  }, [fetchMore, pageInfo, seed]);

  const client = useApolloClient();
  const [pinItem] = useMutation(ACTIVITY_FEED_PIN);
  const [unpinItem] = useMutation(ACTIVITY_FEED_UNPIN);
  /* Scoped to the item that failed — the feed renders one card per node, so
     a bare message string would surface on every card at once. */
  const [pinError, setPinError] = useState<{
    itemId: string;
    message: string;
  } | null>(null);

  /* Patches the cached feed in place rather than refetching. A refetch would
     ask for `first: INITIAL_PAGE_SIZE` and overwrite the entry that
     fetchMore has been merging every loaded page into, collapsing a deeply
     scrolled feed back to one page. The server's canonical ordering lands on
     the next genuine load; this just keeps the card honest until then. */
  const applyPinLocally = useCallback(
    (itemId: string, pinned: boolean) => {
      client.cache.updateQuery(
        {
          query: ACTIVITY_FEED,
          variables: { seed, first: INITIAL_PAGE_SIZE },
        },
        (prev) => {
          const nodes = prev?.activityFeed?.nodes;
          if (!nodes) return prev;

          const next = nodes.map((node: IActivityFeedNode) =>
            node.item?.id === itemId ? { ...node, pinned } : node,
          );
          const index = next.findIndex(
            (node: IActivityFeedNode) => node.item?.id === itemId,
          );
          // Pinned entries sort to the top, newest pin first.
          if (pinned && index > 0) next.unshift(...next.splice(index, 1));

          return {
            ...prev,
            activityFeed: { ...prev.activityFeed, nodes: next },
          };
        },
      );
    },
    [client, seed],
  );

  const togglePin = useCallback(
    async (itemId: string, currentlyPinned: boolean) => {
      setPinError(null);
      const mutate = currentlyPinned ? unpinItem : pinItem;
      const payloadKey = currentlyPinned
        ? "activityFeedUnpin"
        : "activityFeedPin";
      try {
        const result = await mutate({ variables: { input: { itemId } } });
        const errors = result.data?.[payloadKey]?.errors ?? [];
        if (errors.length) {
          const { code, message } = errors[0];
          setPinError({
            itemId,
            message:
              code === "LIMIT_EXCEEDED"
                ? `You can pin up to ${MAX_PINNED_ITEMS} items — unpin one first.`
                : code === "NOT_FOUND"
                ? "That item can't be pinned."
                : message || "Couldn't update your pins. Please try again.",
          });
          return false;
        }
        applyPinLocally(itemId, !currentlyPinned);
        return true;
      } catch {
        // A top-level UNAUTHORIZED lands here rather than in payload errors.
        setPinError({
          itemId,
          message: "Couldn't update your pins. Please try again.",
        });
        return false;
      }
    },
    [pinItem, unpinItem, applyPinLocally],
  );

  return {
    nodes: (data?.activityFeed?.nodes ?? []) as IActivityFeedNode[],
    togglePin,
    pinError,
    dismissPinError: () => setPinError(null),
    loading,
    error,
    loadMoreError,
    hasNextPage: pageInfo?.hasNextPage ?? false,
    loadMore,
    refetch,
  };
};
